import { useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { Modal as ModalBS } from 'react-bootstrap'
import isEmpty from 'lodash/isEmpty'
import { useMountedState, useToggle } from 'react-use'
import type { CreditAmountFormat } from '@ztechapps/ai.common'
import type {
  CreditPackage,
  CreateCreditPurchaseArgs,
  CreateCreditPurchaseResult,
  ValidateCouponArgs,
  ValidateCouponResult,
} from '@ztechapps/common.contract'
import type { ModalTriggerProps } from '@ztechapps/webui.modal'
import { useActionMutation, useDocument, useLazyDocument, useList, useToast } from '@ztechapps/common.hook'
import { formatCreditAmount } from '@ztechapps/ai.common'
import { formatNumber, getWindow } from '@ztechapps/common.utils'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { Modal, ModalDragHandle } from '@ztechapps/webui.modal'
import { TextInput } from '@ztechapps/webui.input'
import { DataSuspense, EmptyContent, HorizontalStack, ShowIf, Spinner } from '@ztechapps/webui.util'
import {
  ALL_CREDIT_PACKAGE_QUERY,
  CREATE_CREDIT_PURCHASE_MUTATION,
  VALIDATE_COUPON_QUERY,
  currencyRateQueries,
} from '../graphql'

type BuyCreditsContentProps = { onClose: () => void }

// Rendered inside the Modal's render prop, so it mounts on open and unmounts on close — every
// open starts with no package or coupon state left over from the previous one.
const BuyCreditsContent = ({ onClose }: BuyCreditsContentProps) => {
  const client = useApolloClient()
  const { showError, showSuccess } = useToast()
  // `list` is `undefined` (not `[]`) on every render before `allCreditPackage` resolves
  // (useBuildListQueryResults.ts does `get(data, entity, undefined)`) — default it here so
  // `.find()`/`.map()` below never run against `undefined`.
  const {
    list,
    loading: packagesLoading,
    error: packagesError,
  } = useList<CreditPackage>({
    query: ALL_CREDIT_PACKAGE_QUERY,
    entity: 'allCreditPackage',
    filter: { active: true },
    sort: { sortOrder: 1 },
  })
  const packages = list ?? []

  // The catalog is priced in a single currency, so the first package's currency picks the one
  // CurrencyRate that formats every price here.
  const currency = packages[0]?.currency
  const { document: currencyRate, loading: currencyRateLoading } = useDocument<CreditAmountFormat>({
    ...currencyRateQueries.singleConfig,
    filter: { currency },
    options: { queryOptions: { skip: !currency } },
  })
  const formatPrice = (amount?: number) => formatCreditAmount(amount, currencyRate)
  // The picker shows packages only once they and their currency have loaded without error; the
  // footer follows it, so a failed or empty catalog leaves no Pay button with nothing to pay for.
  const packagesPending = packagesLoading || currencyRateLoading
  const noPackages = isEmpty(packages)
  const packagesReady = !packagesPending && !packagesError && !noPackages

  const [selectedId, setSelectedId] = useState<string>()
  const [couponInput, setCouponInput] = useState('')
  const [appliedCoupon, setAppliedCoupon] = useState<ValidateCouponResult>()
  const [couponError, setCouponError] = useState<string>()

  // `validateCoupon` is a GraphQL Query, not a Mutation — Apollo's `useMutation` throws
  // "Running a Mutation requires a graphql Mutation, but a Query was used instead" if handed a
  // Query document — so it runs on demand via `executeQuery` on "Apply". `executeQuery` resolves
  // with the raw `{ validateCoupon }` response (and the failure in `error`, never a rejection),
  // so the type parameter is the response shape rather than an entity. The empty `filter` only
  // keeps `useLazyDocument` from warning that it has no filter to skip on.
  const { executeQuery: validateCoupon, loading: validatingCoupon } = useLazyDocument<{
    validateCoupon: ValidateCouponResult
  }>({
    query: VALIDATE_COUPON_QUERY,
    entity: 'validateCoupon',
    filter: {},
    options: { queryOptions: { fetchPolicy: 'network-only' } },
  })
  const [runCreateCreditPurchase, { loading: buying }] = useActionMutation<
    CreateCreditPurchaseResult,
    CreateCreditPurchaseArgs
  >({ query: CREATE_CREDIT_PURCHASE_MUTATION })
  // Esc closes the modal while the purchase request is in flight, so it can unmount before
  // `handleBuy` resumes. `onClose` is the shared Modal's absolute close — called late, it would
  // close a modal the user has reopened since.
  const isMounted = useMountedState()
  // `buying` only covers the mutation; once it resolves with a checkout URL the tab is still on its
  // way to Stripe, so this flag spans the whole purchase — from the click until the page unloads.
  // Without it Pay is clickable again after the request and a second click creates a second
  // Pending CreditPurchase.
  const [paying, setPaying] = useToggle(false)

  const selectedPackage = packages.find((pkg) => pkg._id === selectedId)
  const discountAmount = appliedCoupon?.discountAmount ?? 0
  const totalToPay = selectedPackage ? (appliedCoupon?.finalAmount ?? selectedPackage.price) : 0

  const handleSelectPackage = (pkg: CreditPackage) => {
    setSelectedId(pkg._id)
    // A coupon's discount is validated against a specific package price — a different pick
    // invalidates whatever was already applied, so clear it rather than show a stale discount.
    setAppliedCoupon(undefined)
    setCouponError(undefined)
  }

  const handleApplyCoupon = async () => {
    const code = couponInput.trim()
    if (!code || !selectedPackage) return

    setCouponError(undefined)
    const input: ValidateCouponArgs = { code, amount: selectedPackage.price }
    const { data, error } = await validateCoupon({ variables: { input } })
    const validated = data?.validateCoupon
    if (error || !validated) {
      setAppliedCoupon(undefined)
      setCouponError(error?.message ?? 'Coupon validation returned no result')
      return
    }
    setAppliedCoupon(validated)
  }

  const handleBuy = async () => {
    if (!selectedPackage) return

    setPaying(true)
    try {
      const purchase = await runCreateCreditPurchase({
        creditPackageId: selectedPackage._id,
        couponCode: appliedCoupon?.coupon.code,
      })

      // A 100%-off coupon settles inline server-side, so the balance is already credited and there is
      // no checkout to open. The refetch puts the new balance on the AI Usage card; it is best-effort
      // because the purchase already settled, so its failure must not reach the catch below.
      if (purchase?.paid) {
        showSuccess({ message: `${formatNumber(selectedPackage.credits)} credits have been added to your balance.` })
        if (isMounted()) onClose()
        await client.refetchQueries({ include: ['myCreditBalance'] }).catch(() => undefined)
        return
      }

      if (!purchase?.checkoutUrl) throw new Error('Checkout could not be started. Try Pay again.')
      // Same tab, as `apps/panelpunches/store/src/components/PayBalanceButton/usePayBalance.ts:66` does.
      // Pay stays busy: the page unloads for the checkout, so there is nothing to free it for.
      getWindow().location.href = purchase.checkoutUrl
    } catch (error) {
      showError({ message: (error as Error)?.message ?? 'Purchase failed. Try Pay again.' })
      setPaying(false)
    }
  }

  return (
    <>
      <ModalDragHandle closeButton={!buying}>
        <ModalBS.Title>Buy Credits</ModalBS.Title>
      </ModalDragHandle>

      <ModalBS.Body>
        <div className="mb-3">
          <small className="text-muted">Choose a package</small>
          <div className="mt-2">
            <DataSuspense
              loading={packagesPending}
              loadingComponent={<Spinner />}
              error={packagesError}
              isEmpty={noPackages}
              emptyComponent={<EmptyContent message="No credit packages available." />}
            >
              <HorizontalStack gap={2} className="flex-wrap">
                {packages.map((pkg) => (
                  <BusyButton
                    key={pkg._id}
                    variant={pkg._id === selectedId ? 'primary' : 'outline-secondary'}
                    text={`${formatNumber(pkg.credits)} credits — ${formatPrice(pkg.price)}`}
                    disabled={paying}
                    onClick={() => handleSelectPackage(pkg)}
                  />
                ))}
              </HorizontalStack>
            </DataSuspense>
          </div>
        </div>

        <ShowIf condition={!!selectedPackage}>
          <div className="mb-3">
            <small className="text-muted">Have a coupon?</small>
            <HorizontalStack gap={2} className="mt-2">
              <TextInput
                placeholder="e.g. SAVE20"
                value={couponInput}
                disabled={paying}
                onChange={(e) => {
                  setCouponInput(e.target.value)
                  setCouponError(undefined)
                }}
              />
              <BusyButton
                variant="outline-primary"
                text="Apply"
                isBusy={validatingCoupon}
                disabled={paying || !couponInput.trim()}
                onClick={handleApplyCoupon}
              />
            </HorizontalStack>
            <ShowIf condition={!!couponError}>
              <div className="text-danger-emphasis text-break mt-1">
                <small>{couponError}</small>
              </div>
            </ShowIf>
            <ShowIf condition={!!appliedCoupon}>
              <div className="text-success-emphasis mt-1">
                <small>
                  {appliedCoupon?.coupon.code} applied — save {formatPrice(discountAmount)}.
                </small>
              </div>
            </ShowIf>
          </div>

          <div className="border rounded p-3 mb-3">
            <HorizontalStack className="justify-content-between">
              <small className="text-muted">Package — {formatNumber(selectedPackage?.credits)} credits</small>
              <small>{formatPrice(selectedPackage?.price)}</small>
            </HorizontalStack>
            <ShowIf condition={!!appliedCoupon}>
              <HorizontalStack className="justify-content-between">
                <small className="text-muted">Coupon {appliedCoupon?.coupon.code}</small>
                <small>-{formatPrice(discountAmount)}</small>
              </HorizontalStack>
            </ShowIf>
            <HorizontalStack className="justify-content-between border-top mt-2 pt-2">
              <span className="fw-semibold">Total to pay</span>
              <span className="fw-semibold">{formatPrice(totalToPay)}</span>
            </HorizontalStack>
          </div>
        </ShowIf>
      </ModalBS.Body>

      <ShowIf condition={packagesReady}>
        <ModalBS.Footer>
          <SaveCancelButtons
            saveText={selectedPackage ? `Pay ${formatPrice(totalToPay)}` : 'Select a package'}
            saveIcon="credit-card"
            saveBusyText="Waiting for payment..."
            saveTitle="Pay for the selected package"
            cancelTitle="Close without paying"
            isBusy={paying}
            cancelDisabled={buying}
            disabled={!selectedPackage}
            onSave={handleBuy}
            onCancel={onClose}
          />
        </ModalBS.Footer>
      </ShowIf>
    </>
  )
}

type BuyCreditsModalProps = { canTopUp: boolean }

export const BuyCreditsModal = ({ canTopUp }: BuyCreditsModalProps) => {
  const trigger = ({ onClick }: ModalTriggerProps) => (
    <BusyButton
      icon="cart-shopping"
      variant="primary"
      text="Buy Credits"
      disabled={!canTopUp}
      disabledReason={canTopUp ? undefined : 'Requires the AI billing permission'}
      onClick={onClick}
    />
  )

  return <Modal trigger={trigger}>{({ toggleShowModal }) => <BuyCreditsContent onClose={toggleShowModal} />}</Modal>
}
